/**
 * Assistant composer (frontend-ux-design.md §4.3 / frontend-display.md §4.2).
 *
 * A borderless `Textarea` on the rail paper with a single `border-t` hairline
 * above it. Send on `⌘↵` (or the Send button); `Stop` cancels an in-flight
 * stream; `Clear` resets the thread — both ghost buttons.
 *
 * SHELL: `onSend` appends a local sample echo; the LATER slice swaps it for
 * `sendMessage()` against the live agent.
 */
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Icon } from "@/components/ui/icon";

interface ComposerProps {
  onSend: (text: string) => void;
  onStop: () => void;
  onClear: () => void;
  isStreaming?: boolean;
  disabled?: boolean;
}

export function Composer({
  onSend,
  onStop,
  onClear,
  isStreaming,
  disabled,
}: ComposerProps) {
  const [value, setValue] = React.useState("");

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // ⌘↵ / Ctrl+↵ sends; plain Enter inserts a newline.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="border-t border-border p-3">
      {/* A bordered card so the composer reads as a deliberate input surface
          (feedback #2: restore borders) with a black-accent focus ring. */}
      <div className="rounded-lg border border-input bg-card p-2 shadow-sm focus-within:ring-2 focus-within:ring-ring">
        <Textarea
          data-testid="assistant-composer-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          disabled={disabled}
          placeholder="Ask the assistant…  (⌘↵ to send)"
          aria-label="Message the assistant"
          className="min-h-0 resize-none border-none bg-transparent px-1 shadow-none focus-visible:ring-0"
        />
        <div className="mt-1 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {isStreaming ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onStop}
              data-testid="assistant-stop"
            >
              <Icon name="stop" size={16} aria-hidden />
              Stop
            </Button>
          ) : null}
          </div>
          <Button
            size="sm"
            onClick={submit}
            disabled={!value.trim() || disabled}
            data-testid="assistant-send"
            aria-label="Send message"
          >
            <Icon name="send" size={16} aria-hidden />
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Composer;
